/**
 * ATL-001214 (finding #5) — Route-level guard for the lane-mode-specific
 * Rating sub-pages (Lane Location Rates / Lane City Rates).
 *
 * The Rating sidebar menu already shows exactly one of these two (plus
 * Billing Rates) based on the org's `customer:rating.configJson.rate_type`
 * preference, but the routes in App.tsx stay registered for ALL of them
 * (intentionally — so a deep link still resolves if the preference
 * changes mid-session). That meant a user could type
 * `/rating/lane-location-rates` directly while the org is in
 * `Lanes/Cities` mode and reach a fully-functional page — ADD LANE and
 * all — for a mode the org isn't actually using. V1 never exposes a page
 * outside the chosen mode by any path. This closes that gap the same way
 * `RequireFeature` closes the equivalent one for disabled modules:
 * redirect instead of rendering.
 *
 * Billing Rates / FSC Rates / Accessorial Rates don't need this guard —
 * Billing Rates already has nothing to gate against here (every other
 * mode hides it, but it's also the ultimate fallback — see
 * `rateTypeMode` default below), and FSC/Accessorial are mode-independent
 * per the ticket's own "same as V1" findings.
 */
import { Navigate, Outlet } from 'react-router-dom';
import { useEnterpriseFeatures } from '../../../hooks/use-enterprise-features';
import { Spinner } from '../ui/spinner';

export type RatingMode = 'Rate Types' | 'Lanes/Locations' | 'Lanes/Cities';

interface RequireRatingModeProps {
  mode: RatingMode;
  children?: React.ReactNode;
}

export function RequireRatingMode({ mode, children }: RequireRatingModeProps) {
  const { data: features, isError } = useEnterpriseFeatures();

  // Query failed — fail open, matching RequireFeature's stance. The
  // server-side rate-save endpoints remain the real security boundary.
  if (isError) {
    return children !== undefined ? <>{children}</> : <Outlet />;
  }

  if (!features) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  const ratingFeature = features.find((f) => f.key === 'customer:rating');
  const ratingPrefs = (ratingFeature?.configJson ?? {}) as Record<string, unknown>;
  // Mirrors Sidebar.tsx's own `rateTypeMode` derivation exactly — same
  // default so this guard never disagrees with what the menu shows.
  const rateTypeMode = (ratingPrefs.rate_type as string) || 'Rate Types';

  if (rateTypeMode !== mode) {
    return <Navigate to="/dashboard" replace />;
  }

  return children !== undefined ? <>{children}</> : <Outlet />;
}

export default RequireRatingMode;
